"use client";
import type { BlogRelatedCollection } from "@v2/types/blog";
import { Box, Button, Typography } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import Link from "next/link";
import { ChevronLeft } from "lucide-react";

interface Props {
    collection: BlogRelatedCollection;
    vertical?: boolean;
}

export default function BlogRelatedCollection({ collection, vertical = false }: Props) {
    const bg = collection.background_color || "#f5f5f5";
    const textColor = collection.text_color || "#ffffff";

    if (vertical) {
        return (
            <Box
                component={Link}
                href={`/fa/collection/${collection.slug}`}
                prefetch={false}
                aria-label={`مشاهده کالکشن ${collection.title}`}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    textDecoration: "none",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: bg,
                    // width: "50%",
                    aspectRatio: "3/4",
                    transition: "box-shadow 180ms ease, transform 180ms ease",
                    "&:hover": {
                        boxShadow: "rgba(0,0,0,0.12) 0px 8px 24px",
                        transform: "translateY(-2px)",
                    },
                }}
            >
                {/* image — fixed height */}
                <Box sx={{ flex: 1, width: 1, height: 100, position: "relative", display: 'flex', justifyContent: 'center', flexDirection: 'column', flexShrink: 0 }}>
                    {collection.image?.id ? (
                        <MediaV2
                            media_id={String(collection.image.id)}
                            alt={collection.image.alt || collection.title}
                            options={{ action: "thumbnail", quality: 90 }}
                            width={200}
                            imageProps={{ fill: true, style: { objectFit: "contain" }, loading: "lazy" }}
                            boxProps={{ sx: { width: 1, aspectRatio: '3/4', maxHeight: 95 } }}
                        />
                    ) : null}
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25, mt: 2 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: textColor, textAlign: "center", lineHeight: 1.4 }}>
                            {collection.title}
                        </Typography>
                        {(collection.brand.fa_name || collection.brand.en_name) && (
                            <Typography sx={{ fontSize: 10, color: textColor, opacity: 0.75, textAlign: "center" }}>
                                از برند {collection.brand.fa_name || collection.brand.en_name}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* content */}
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pt: 0.5,
                    pb: 1.5,
                    px: 1.5,
                    gap: 1,
                }}>

                    <Button
                        component="span"
                        variant="contained"
                        disableElevation
                        endIcon={<ChevronLeft size={13} />}
                        sx={{
                            fontSize: 12,
                            height: 34,
                            px: 2,
                            bgcolor: "rgba(255,255,255,0.2)",
                            color: textColor,
                            backdropFilter: "blur(4px)",
                            border: `1px solid ${textColor}40`,
                            "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                        }}
                    >
                        مشاهده کالکشن
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            component={Link}
            href={`/fa/collection/${collection.slug}`}
            prefetch={false}
            aria-label={`مشاهده کالکشن ${collection.title}`}
            sx={{
                display: "flex",
                textDecoration: "none",
                borderRadius: 1.5,
                overflow: "hidden",
                bgcolor: bg,
                mb: 2,
                transition: "box-shadow 180ms ease, transform 180ms ease",
                "&:hover": {
                    boxShadow: "rgba(0,0,0,0.12) 0px 8px 24px",
                    transform: "translateY(-2px)",
                },
            }}
        >
            {/* content — right side in RTL */}
            <Box sx={{
                width: "50%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 2,
                px: 1.5,
                gap: 1,
            }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: textColor, textAlign: "center", lineHeight: 1.4 }}>
                    {collection.title}
                </Typography>
                {(collection.brand.fa_name || collection.brand.en_name) && (
                    <Typography sx={{ fontSize: 11, color: textColor, opacity: 0.75, textAlign: "center" }}>
                        از برند {collection.brand.fa_name || collection.brand.en_name}
                    </Typography>
                )}
                <Button
                    component="span"
                    variant="contained"
                    disableElevation
                    endIcon={<ChevronLeft size={13} />}
                    sx={{
                        fontSize: 12,
                        height: 34,
                        px: 2,
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: textColor,
                        backdropFilter: "blur(4px)",
                        border: `1px solid ${textColor}40`,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                    }}
                >
                    مشاهده کالکشن
                </Button>
            </Box>

            {/* collection image — left side in RTL */}
            <Box sx={{ width: "50%", position: "relative", minHeight: 130 }}>
                {collection.image?.id ? (
                    <MediaV2
                        media_id={String(collection.image.id)}
                        alt={collection.image.alt || collection.title}
                        options={{ action: "thumbnail", quality: 90 }}
                        imageProps={{ fill: true, style: { objectFit: "contain" }, loading: "lazy" }}
                        boxProps={{ sx: { width: 1, height: 1 } }}
                    />
                ) : null}
            </Box>
        </Box>
    );
}
